import React, { CSSProperties, useEffect } from 'react'
import {
  Stack,
  Box,
  useTheme,
  CircularProgress,
  IconButton,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import { useMemo } from 'react'
import { memo } from 'react'
import { NamedExoticComponent } from 'react'
import { useState } from 'react'
import { PrescriptionImageLightbox } from './PrescriptionImageLightbox'
import { NaturalSize } from '../../helpers/imageRotation'

/**
 * 操作列（‹ › ↻ ⤢ とライトボックス）。呼び出し側が有効にしたときだけ渡される。
 * 渡されなければ従来の黒ピル「n/mページ中 ›」だけを描く。
 */
export type ViewerControls = {
  /** 表示中の角度（自動傾き補正 + 手動回転、deg）。ライトボックスが同じ角度で出す */
  rotation: number
  /** インライン画像の自然サイズ。読み込み前は null で、↻ ⤢ を無効にする */
  naturalSize: NaturalSize | null
  onPrevClick: () => void
  onRotateClick: () => void
}

type Props = {
  imagesLength: number
  imageIndex: number
  imageUrl: string
  onPageIndicatorClick: () => void
  onImageClick: React.MouseEventHandler<HTMLImageElement>
  onLoad: React.ReactEventHandler<HTMLImageElement>
  imageStyle: CSSProperties
  styleForSmallScreen: CSSProperties
  containerAspectRatio: string
  viewerControls?: ViewerControls
}

const CONTROL_ICON_SIZE = 18
// 黒ピルの上では既定の disabled 色（黒の 26%）が見えないので、色は継ぎ opacity で落とす
const controlSx = { '&.Mui-disabled': { color: 'inherit', opacity: 0.4 } }

export const PrescriptionImageBox: NamedExoticComponent<Props> = memo(
  ({
    imagesLength,
    imageIndex,
    imageUrl,
    onPageIndicatorClick,
    onImageClick,
    onLoad,
    imageStyle,
    styleForSmallScreen,
    containerAspectRatio,
    viewerControls,
  }) => {
    const theme = useTheme()
    const [loading, setLoading] = useState(true)
    const [lightboxOpen, setLightboxOpen] = useState(false)

    useEffect(() => {
      const image = new Image()
      image.onload = () => {
        setLoading(false)
      }
      image.src = imageUrl
      // imageUrlが変わった時は再度loading状態にする。開いていたライトボックスも閉じる
      return () => {
        setLoading(true)
        setLightboxOpen(false)
      }
    }, [imageUrl])

    const PrescImage = useMemo(
      () => (
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: containerAspectRatio,
            overflow: 'hidden',
          }}
        >
          <img
            src={imageUrl}
            style={imageStyle}
            onLoad={onLoad}
            onClick={onImageClick}
          />
        </div>
      ),
      [imageUrl, imageStyle, containerAspectRatio, onLoad, onImageClick]
    )
    const pillSx = {
      [theme.breakpoints.down('md')]: {
        right: 24,
      },
    }
    const hasMultiplePages = imagesLength > 1
    const imageReady = viewerControls?.naturalSize != null
    return (
      <Box
        sx={{
          [theme.breakpoints.down('md')]: styleForSmallScreen,
        }}
        width="50%"
      >
        <Stack
          sx={{
            [theme.breakpoints.up('md')]: {
              position: 'relative',
            },
            height: '100%', // Make sure the Stack takes full height
            justifyContent: 'center', // Center children horizontally
            alignItems: 'center', // Center children vertically
          }}
          direction="row"
          bgcolor="#FFF"
          p={1}
        >
          {loading ? (
            <CircularProgress
              style={{
                color: theme.palette.secondary.dark,
                opacity: 0.3,
              }}
              size={80}
            />
          ) : (
            PrescImage
          )}
          {viewerControls ? (
            <>
              <Stack
                direction="row"
                alignItems="center"
                sx={pillSx}
                position="absolute"
                top={8}
                right={24}
                bgcolor="#000"
                color="#fff"
                p={1}
                borderRadius="8px"
                style={{ opacity: 0.6 }}
              >
                {hasMultiplePages && (
                  <IconButton
                    aria-label="前のページ"
                    size="small"
                    color="inherit"
                    onClick={viewerControls.onPrevClick}
                  >
                    <ChevronLeftIcon sx={{ fontSize: CONTROL_ICON_SIZE }} />
                  </IconButton>
                )}
                <span>{`${imageIndex + 1}/${imagesLength}`}</span>
                {hasMultiplePages && (
                  <IconButton
                    aria-label="次のページ"
                    size="small"
                    color="inherit"
                    onClick={onPageIndicatorClick}
                  >
                    <ChevronRightIcon sx={{ fontSize: CONTROL_ICON_SIZE }} />
                  </IconButton>
                )}
                <IconButton
                  aria-label="回転"
                  size="small"
                  color="inherit"
                  sx={controlSx}
                  disabled={!imageReady}
                  onClick={viewerControls.onRotateClick}
                >
                  <RotateRightIcon sx={{ fontSize: CONTROL_ICON_SIZE }} />
                </IconButton>
                <IconButton
                  aria-label="拡大表示"
                  size="small"
                  color="inherit"
                  sx={controlSx}
                  disabled={!imageReady}
                  onClick={() => setLightboxOpen(true)}
                >
                  <OpenInFullIcon sx={{ fontSize: CONTROL_ICON_SIZE }} />
                </IconButton>
              </Stack>
              {lightboxOpen && viewerControls.naturalSize && (
                <PrescriptionImageLightbox
                  imageUrl={imageUrl}
                  rotation={viewerControls.rotation}
                  size={viewerControls.naturalSize}
                  onClose={() => setLightboxOpen(false)}
                />
              )}
            </>
          ) : (
            <Box
              sx={pillSx}
              position="absolute"
              top={8}
              right={24}
              bgcolor="#000"
              color="#fff"
              p={1}
              borderRadius="8px"
              style={{ opacity: 0.6 }}
              onClick={onPageIndicatorClick}
            >
              {`${imageIndex + 1}/${imagesLength}ページ中 ›`}
            </Box>
          )}
        </Stack>
      </Box>
    )
  }
)

PrescriptionImageBox.displayName = 'PrescriptionImageBox'
